import React, { useEffect, useMemo, useState } from 'react';
import { app, HostClientType } from '@microsoft/teams-js';
import { TeamsEmbedProvider } from '../context/TeamsEmbedContext';
import { ensureTeamsInitialized, isTeamsSdkReady, setTeamsHostClientType } from '../utils/teamsSdk';
import { isTeamsEmbeddedOnLoad } from '../utils/teamsHost';

interface TeamsContextInfo {
  theme: string;
  locale: string;
  entityId?: string;
  channelId?: string;
  teamId?: string;
  chatId?: string;
  userId?: string;
  userPrincipalName?: string;
  isInTeams: boolean;
}

interface TeamsIntegrationProps {
  children: React.ReactNode;
  onTeamsContext?: (context: TeamsContextInfo) => void;
}

let teamsContentNotifySuccessSent = false;
let teamsThemeHandlerRegistered = false;

const TeamsIntegration: React.FC<TeamsIntegrationProps> = ({ children, onTeamsContext }) => {
  const embeddedOnLoad = useMemo(() => {
    return isTeamsEmbeddedOnLoad();
  }, []);
  const [sdkReady, setSdkReady] = useState(false);
  const [teamsContext, setTeamsContext] = useState<TeamsContextInfo>({
    theme: 'default',
    locale: 'en-US',
    isInTeams: embeddedOnLoad,
  });
  const [isLoading, setIsLoading] = useState(embeddedOnLoad);

  const teamsHostActive = embeddedOnLoad || sdkReady || teamsContext.isInTeams;

  useEffect(() => {
    if (!teamsHostActive) return;
    document.body.classList.add('teams-embedded');
  }, [teamsHostActive]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!embeddedOnLoad) {
        // Not the main Teams tab — auth popups call ensureTeamsInitialized() directly
        // via notifyTeamsAuthSuccess/Failure and must not be interfered with here.
        setIsLoading(false);
        return;
      }
      try {
        await ensureTeamsInitialized();
        if (cancelled) return;

        // Only mark ready when the SDK genuinely connected to a Teams host.
        // ensureTeamsInitialized() may return silently (no throw) for "no parent window".
        if (!app.isInitialized()) return;

        setSdkReady(true);
        setTeamsContext((prev) => ({ ...prev, isInTeams: true }));

        const context = await app.getContext();
        if (cancelled) return;

        setTeamsHostClientType(context.app?.host?.clientType as HostClientType | undefined);

        const teamsInfo: TeamsContextInfo = {
          theme: context.app?.theme || 'default',
          locale: context.app?.locale || 'en-US',
          entityId: context.page?.id,
          channelId: context.channel?.id,
          teamId: context.team?.groupId,
          chatId: context.chat?.id,
          userId: context.user?.id,
          userPrincipalName: context.user?.userPrincipalName,
          isInTeams: true,
        };

        setTeamsContext(teamsInfo);
        onTeamsContext?.(teamsInfo);

        if (!teamsThemeHandlerRegistered) {
          teamsThemeHandlerRegistered = true;
          app.registerOnThemeChangeHandler((theme: string) => {
            setTeamsContext((prev) => ({ ...prev, theme }));
          });
        }

        if (!teamsContentNotifySuccessSent) {
          teamsContentNotifySuccessSent = true;
          app.notifySuccess();
        }
      } catch (error: unknown) {
        const msg = error instanceof Error ? error.message : String(error);
        // "No Parent window found" is expected when testing with ?teams=true in a plain browser,
        // or when Teams hasn't set up its frame yet. Treat as a soft failure.
        const isExpected = /no parent window|initialization failed/i.test(msg);
        if (isExpected) {
          console.warn('[TeamsIntegration] SDK init skipped (no Teams frame):', msg);
        } else if (embeddedOnLoad) {
          console.error('Teams initialization error:', error);
          try {
            app.notifyFailure({
              reason: app.FailedReason.Other,
              message: msg,
            });
          } catch {
            // notifyFailure may throw if the app frame is not available.
          }
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [embeddedOnLoad, onTeamsContext]);

  useEffect(() => {
    if (teamsContext.isInTeams) {
      document.body.className = `teams-theme-${teamsContext.theme}`;
    }
  }, [teamsContext.theme, teamsContext.isInTeams]);

  if (isLoading && embeddedOnLoad && !isTeamsSdkReady()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Teams integration...</p>
        </div>
      </div>
    );
  }

  return (
    <TeamsEmbedProvider embedded={teamsHostActive}>
      <div className={`teams-integration ${teamsHostActive ? 'in-teams' : 'standalone'}`}>
        {children}
      </div>
    </TeamsEmbedProvider>
  );
};

export { TeamsIntegration, type TeamsContextInfo };
export default TeamsIntegration;
